import { useLocation } from "react-router-dom";

import { ContentType } from "@seneca/course-service-types";

import {
  getAssignmentCourseId,
  getAssignmentSectionBestScore
} from "seneca-common/features/assignments/features/student-assignments/state";
import {
  getHasHardestQuestionContent,
  makeGetActiveSectionSessionId
} from "seneca-common/features/session/selectors/session";
import {
  useMemoSelector,
  useSelectorOnConditionWithArgs,
  useSelectorWithArgs
} from "seneca-common/utils/hooks/selectors";

import {
  RowLayoutLinkConditional,
  SectionRowNumber,
  SectionRowPercentage,
  SectionRowTitle
} from "features/classroom/features/sidebar/features/common";
import {
  useSectionRowClickWithAnalytics,
  useSectionRowQuitSessionLink
} from "features/classroom/features/sidebar/features/common/hooks";
import { linkToAssignmentStartSession } from "features/classroom/routes/links";

type Props = {
  assignmentId: string;
  activeSectionId: string;
  sectionId: string;
  contentType?: ContentType;
};

export default function AssignmentsSectionRow({
  assignmentId,
  activeSectionId,
  sectionId,
  contentType
}: Props) {
  const courseId = useSelectorWithArgs(getAssignmentCourseId, assignmentId);

  const bestScore = useSelectorWithArgs(
    getAssignmentSectionBestScore,
    assignmentId,
    sectionId,
    contentType
  );

  const location = useLocation();
  const linkToStartLearning = linkToAssignmentStartSession(
    courseId,
    sectionId,
    {
      locationFrom: location.pathname,
      assignmentId,
      contentType
    }
  );

  const link = useSectionRowQuitSessionLink({
    courseId,
    activeSectionId,
    link: linkToStartLearning
  });

  const handleClick = useSectionRowClickWithAnalytics(
    sectionId,
    courseId,
    "Assignments"
  );

  const sessionId = useMemoSelector(makeGetActiveSectionSessionId, sectionId);
  const hasHardestQuestionContent = useSelectorOnConditionWithArgs(
    !!sessionId,
    getHasHardestQuestionContent,
    sessionId
  );

  const isRowActive =
    sectionId === activeSectionId &&
    !!sessionId &&
    (contentType === ContentType.hardestQuestions
      ? !!hasHardestQuestionContent
      : !hasHardestQuestionContent);

  return (
    <RowLayoutLinkConditional
      link={link}
      active={isRowActive}
      onClick={handleClick}
    >
      {{
        title: (
          <>
            <SectionRowNumber courseId={courseId} sectionId={sectionId} />
            <SectionRowTitle
              sectionId={sectionId}
              contentType={contentType}
              dataTestName="AssignmentSection"
            />
          </>
        ),
        indicator: <SectionRowPercentage value={bestScore} />
      }}
    </RowLayoutLinkConditional>
  );
}
